import { Fragment, ReactNode } from 'react';

/**
 * Lightweight markdown renderer tuned for AI coach replies.
 * Supports: headings (#..####), bold (**), italic (*), inline code (`),
 * fenced code blocks (```), unordered (-, *) and ordered (1.) lists,
 * blockquotes (>), horizontal rules (---), links [text](url), and paragraphs.
 * Deliberately small to avoid pulling react-markdown into the bundle.
 */

interface Props {
  text: string;
  variant?: 'default' | 'kid';
}

interface Block {
  type: 'p' | 'h1' | 'h2' | 'h3' | 'h4' | 'ul' | 'ol' | 'quote' | 'code' | 'hr';
  content: string;
  items?: string[];
  lang?: string;
}

function parseBlocks(src: string): Block[] {
  const lines = src.replace(/\r\n/g, '\n').split('\n');
  const blocks: Block[] = [];
  let i = 0;
  while (i < lines.length) {
    const line = lines[i];

    if (/^```/.test(line)) {
      const lang = line.replace(/^```/, '').trim();
      const buf: string[] = [];
      i++;
      while (i < lines.length && !/^```/.test(lines[i])) {
        buf.push(lines[i]);
        i++;
      }
      i++;
      blocks.push({ type: 'code', content: buf.join('\n'), lang });
      continue;
    }

    if (/^\s*$/.test(line)) {
      i++;
      continue;
    }

    if (/^---\s*$/.test(line) || /^___\s*$/.test(line)) {
      blocks.push({ type: 'hr', content: '' });
      i++;
      continue;
    }

    const heading = line.match(/^(#{1,4})\s+(.*)$/);
    if (heading) {
      const level = heading[1].length;
      blocks.push({ type: `h${level}` as Block['type'], content: heading[2].trim() });
      i++;
      continue;
    }

    if (/^>\s?/.test(line)) {
      const buf: string[] = [];
      while (i < lines.length && /^>\s?/.test(lines[i])) {
        buf.push(lines[i].replace(/^>\s?/, ''));
        i++;
      }
      blocks.push({ type: 'quote', content: buf.join('\n') });
      continue;
    }

    if (/^\s*[-*]\s+/.test(line)) {
      const items: string[] = [];
      while (i < lines.length && /^\s*[-*]\s+/.test(lines[i])) {
        items.push(lines[i].replace(/^\s*[-*]\s+/, ''));
        i++;
      }
      blocks.push({ type: 'ul', content: '', items });
      continue;
    }

    if (/^\s*\d+\.\s+/.test(line)) {
      const items: string[] = [];
      while (i < lines.length && /^\s*\d+\.\s+/.test(lines[i])) {
        items.push(lines[i].replace(/^\s*\d+\.\s+/, ''));
        i++;
      }
      blocks.push({ type: 'ol', content: '', items });
      continue;
    }

    const buf: string[] = [line];
    i++;
    while (i < lines.length && lines[i].trim() !== '' && !/^(#{1,4}\s|>\s|```|\s*[-*]\s|\s*\d+\.\s|---|___)/.test(lines[i])) {
      buf.push(lines[i]);
      i++;
    }
    blocks.push({ type: 'p', content: buf.join(' ') });
  }
  return blocks;
}

function renderInline(text: string, keyPrefix: string): ReactNode[] {
  const out: ReactNode[] = [];
  let i = 0;
  let token = 0;

  const push = (node: ReactNode) => {
    out.push(<Fragment key={`${keyPrefix}-${token++}`}>{node}</Fragment>);
  };

  while (i < text.length) {
    if (text[i] === '`') {
      const end = text.indexOf('`', i + 1);
      if (end !== -1) {
        push(
          <code className="px-1.5 py-0.5 rounded bg-warm-100 text-ink-800 font-mono text-[0.92em] border border-ink-100">
            {text.slice(i + 1, end)}
          </code>,
        );
        i = end + 1;
        continue;
      }
    }
    if (text[i] === '*' && text[i + 1] === '*') {
      const end = text.indexOf('**', i + 2);
      if (end !== -1) {
        push(<strong className="font-semibold text-ink-900">{renderInline(text.slice(i + 2, end), `${keyPrefix}-b${token}`)}</strong>);
        i = end + 2;
        continue;
      }
    }
    if (text[i] === '*' && text[i + 1] !== '*') {
      const end = text.indexOf('*', i + 1);
      if (end !== -1) {
        push(<em className="italic">{renderInline(text.slice(i + 1, end), `${keyPrefix}-i${token}`)}</em>);
        i = end + 1;
        continue;
      }
    }
    if (text[i] === '[') {
      const closeBracket = text.indexOf(']', i + 1);
      if (closeBracket !== -1 && text[closeBracket + 1] === '(') {
        const closeParen = text.indexOf(')', closeBracket + 2);
        if (closeParen !== -1) {
          const label = text.slice(i + 1, closeBracket);
          const url = text.slice(closeBracket + 2, closeParen);
          push(
            <a
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              className="text-accent-700 underline decoration-accent-300 underline-offset-2 hover:text-accent-900"
            >
              {label}
            </a>,
          );
          i = closeParen + 1;
          continue;
        }
      }
    }

    let chunk = '';
    while (i < text.length && !'`*['.includes(text[i])) {
      chunk += text[i];
      i++;
    }
    if (chunk) push(chunk);
    if (i < text.length && '`*['.includes(text[i])) {
      // unmatched marker; emit literal
      push(text[i]);
      i++;
    }
  }
  return out;
}

export default function CoachMarkdown({ text, variant = 'default' }: Props) {
  const blocks = parseBlocks(text);
  const isKid = variant === 'kid';
  const proseSize = isKid ? 'text-[17px] leading-[1.7]' : 'text-[15px] leading-relaxed';
  const headingFont = isKid ? 'font-kid' : 'font-display';

  return (
    <div className={`space-y-3 ${proseSize} text-ink-800`}>
      {blocks.map((b, idx) => {
        const k = `b-${idx}`;
        switch (b.type) {
          case 'h1':
            return (
              <h2 key={k} className={`${headingFont} ${isKid ? 'text-2xl' : 'text-2xl'} text-ink-900 mt-2`}>
                {renderInline(b.content, `${k}-i`)}
              </h2>
            );
          case 'h2':
            return (
              <h3 key={k} className={`${headingFont} ${isKid ? 'text-xl' : 'text-xl'} text-ink-900 mt-1`}>
                {renderInline(b.content, `${k}-i`)}
              </h3>
            );
          case 'h3':
          case 'h4':
            return (
              <h4 key={k} className={`${headingFont} ${isKid ? 'text-lg' : 'text-base'} text-ink-900`}>
                {renderInline(b.content, `${k}-i`)}
              </h4>
            );
          case 'ul':
            return (
              <ul key={k} className="space-y-1.5 pl-1">
                {(b.items || []).map((it, j) => (
                  <li key={`${k}-li-${j}`} className="flex gap-2">
                    <span className={`mt-2 w-1.5 h-1.5 rounded-full flex-shrink-0 ${isKid ? 'bg-sky-500' : 'bg-accent-500'}`} />
                    <span>{renderInline(it, `${k}-li-${j}-i`)}</span>
                  </li>
                ))}
              </ul>
            );
          case 'ol':
            return (
              <ol key={k} className="space-y-1.5 pl-1 list-none counter-reset-[step]">
                {(b.items || []).map((it, j) => (
                  <li key={`${k}-li-${j}`} className="flex gap-3">
                    <span
                      className={`flex-shrink-0 w-6 h-6 grid place-items-center rounded-full text-xs font-semibold ${
                        isKid ? 'bg-sun-200 text-ink-900' : 'bg-accent-100 text-accent-700'
                      }`}
                    >
                      {j + 1}
                    </span>
                    <span>{renderInline(it, `${k}-li-${j}-i`)}</span>
                  </li>
                ))}
              </ol>
            );
          case 'quote':
            return (
              <blockquote
                key={k}
                className={`pl-4 border-l-4 ${isKid ? 'border-sky-300 bg-sky-50' : 'border-accent-300 bg-accent-50'} py-2 pr-3 rounded-r-lg italic text-ink-700`}
              >
                {renderInline(b.content, `${k}-i`)}
              </blockquote>
            );
          case 'code':
            return (
              <pre
                key={k}
                className={`overflow-x-auto rounded-xl border border-ink-100 bg-warm-50 p-4 text-[13px] font-mono text-ink-800 ${isKid ? 'border-sky-200' : ''}`}
              >
                <code>{b.content}</code>
              </pre>
            );
          case 'hr':
            return <hr key={k} className="border-ink-100" />;
          case 'p':
          default:
            return (
              <p key={k} className="text-ink-800">
                {renderInline(b.content, `${k}-i`)}
              </p>
            );
        }
      })}
    </div>
  );
}
